import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton } from '@mui/material';
import { Iconify } from 'src/components';
import { REPORT_METRIC_ACCENTS } from 'src/components/actionCards/actionCardColors';

const CARD_HEIGHT = 92;

/**
 * "Treatments in Category" KPI card: "N treatments · M active" plus a compact,
 * horizontally scrollable carousel of treatment chips (active first, "Name · leads").
 * Fixed height — the carousel scrolls instead of growing the card. The card itself is
 * not clickable; clicking a chip selects that treatment (click again to clear).
 */
export default function TreatmentChipsCard({ label, chips, selectedId, onSelect, accent }) {
    const scrollRef = useRef(null);
    const total = chips.length;
    const activeCount = chips.filter((c) => c.active).length;

    const scrollBy = (dir) => {
        const el = scrollRef.current;
        if (el?.scrollBy) el.scrollBy({ left: dir * 180, behavior: 'smooth' });
    };

    return (
        <Box sx={{ flex: 1, minWidth: '150px', display: 'flex' }}>
            <Box
                data-testid="treatment-chips-card"
                sx={{
                    position: 'relative',
                    flex: 1,
                    minWidth: 0,
                    height: CARD_HEIGHT,
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    borderRadius: '12px',
                    border: '2px solid #f1f5f9',
                    px: 1.5,
                    pt: 1.25,
                    pb: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    '&::before': {
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px', bgcolor: accent,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '999px', bgcolor: accent, flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>
                        {label}
                    </Typography>
                </Box>
                <Typography data-testid="treatment-chips-summary" sx={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.2 }}>
                    {`${total} treatment${total === 1 ? '' : 's'} · ${activeCount} active`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                    <IconButton size="small" aria-label="Scroll treatments left" onClick={() => scrollBy(-1)} sx={{ p: 0.25 }}>
                        <Iconify icon="eva:chevron-left-fill" width={16} height={16} />
                    </IconButton>
                    <Box
                        ref={scrollRef}
                        data-testid="treatment-chips-scroller"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            gap: 0.75,
                            overflowX: 'auto',
                            scrollSnapType: 'x proximity',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                        }}
                    >
                        {chips.map((chip) => {
                            const selected = String(chip.id) === String(selectedId);
                            let textColor = '#94a3b8';
                            if (selected) textColor = '#fff';
                            else if (chip.active) textColor = '#1e293b';
                            return (
                                <Box
                                    key={chip.id}
                                    component="button"
                                    type="button"
                                    data-testid={`treatment-chip-${chip.id}`}
                                    data-active={chip.active ? 'true' : 'false'}
                                    aria-pressed={selected}
                                    onClick={() => onSelect && onSelect(selected ? null : chip.id)}
                                    sx={{
                                        flexShrink: 0,
                                        scrollSnapAlign: 'start',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        px: 1,
                                        py: 0.625,
                                        borderRadius: '999px',
                                        border: '1px solid',
                                        borderColor: selected ? accent : '#e2e8f0',
                                        bgcolor: selected ? accent : '#f8fafc',
                                        color: textColor,
                                        opacity: chip.active || selected ? 1 : 0.7,
                                    }}
                                >
                                    {`${chip.name} · ${chip.leads}`}
                                </Box>
                            );
                        })}
                    </Box>
                    <IconButton size="small" aria-label="Scroll treatments right" onClick={() => scrollBy(1)} sx={{ p: 0.25 }}>
                        <Iconify icon="eva:chevron-right-fill" width={16} height={16} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}

TreatmentChipsCard.propTypes = {
    label: PropTypes.string,
    chips: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        name: PropTypes.string,
        leads: PropTypes.number,
        active: PropTypes.bool,
    })),
    selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onSelect: PropTypes.func,
    accent: PropTypes.string,
};

TreatmentChipsCard.defaultProps = {
    label: 'Treatments in Category',
    chips: [],
    selectedId: null,
    onSelect: null,
    accent: REPORT_METRIC_ACCENTS.started,
};
